import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useToast } from '../ui/toast';
import { api } from './client';
import type { ProjectDetail } from './types';

export const useProject = (key: string | undefined) =>
  useQuery({
    queryKey: ['project', key],
    queryFn: () => api.get<ProjectDetail>(`/projects/${key}`),
    enabled: !!key,
    retry: false,
  });

/** Mutation with success toast and cache invalidation; errors stay on the mutation for inline display. */
export function useAction<TVars, TResult = unknown>(
  fn: (vars: TVars) => Promise<TResult>,
  opts: {
    success?: string | ((r: TResult) => string);
    invalidate?: QueryKey[];
    onSuccess?: (r: TResult, vars: TVars) => void;
  } = {},
) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (r, vars) => {
      await Promise.all((opts.invalidate ?? []).map((k) => qc.invalidateQueries({ queryKey: k })));
      if (opts.success) toast(typeof opts.success === 'function' ? opts.success(r) : opts.success);
      opts.onSuccess?.(r, vars);
    },
  });
}

export { api };
